import { useState, useCallback } from 'react';
import { message } from 'antd';
import { pwj220Api, FJ750, FJ751, ValidationError } from '../../../services/pwj220Api';

export function usePWJ220Record() {
  const [record, setRecord] = useState<FJ750 | null>(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<ValidationError[]>([]);

  const load = useCallback(async (id: string): Promise<FJ750 | null> => {
    setLoading(true);
    try {
      const data = await pwj220Api.get(id);
      setRecord(data);
      setErrors([]);
      return data;
    } catch (err: any) {
      message.error('載入失敗：' + (err?.response?.data?.error || err.message));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const save = useCallback(async (
    master: Partial<FJ750>,
    details: Partial<FJ751>[],
    isNew: boolean
  ): Promise<FJ750 | null> => {
    setLoading(true);
    setErrors([]);
    try {
      let result: FJ750;
      if (isNew) {
        result = await pwj220Api.create(master, details);
      } else {
        result = await pwj220Api.update(master.fj750_01!, master, details);
      }
      setRecord(result);
      message.success(isNew ? '新增成功' : '儲存成功');
      return result;
    } catch (err: any) {
      if (err?.response?.status === 422) {
        const errs: ValidationError[] = err.response.data.errors || [];
        setErrors(errs);
        message.error('驗證失敗：' + errs.map(e => e.message).join('；'));
      } else {
        message.error('儲存失敗：' + (err?.response?.data?.error || err.message));
      }
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const remove = useCallback(async (id: string): Promise<boolean> => {
    setLoading(true);
    try {
      await pwj220Api.delete(id);
      setRecord(null);
      message.success('刪除成功');
      return true;
    } catch (err: any) {
      message.error('刪除失敗：' + (err?.response?.data?.error || err.message));
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  return { record, loading, errors, load, save, remove, setRecord };
}
